import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import mappingData from '../../../../pbac-registry/feature-policy-mapping.json';
import { useFeatures } from '../../../contexts/FeaturesContext';

// eslint-disable-next-line no-unused-vars
const PermissionsModal = ({ isOpen, onClose, resource, action, user }) => {
  const location = useLocation();
  const { features } = useFeatures();
  const [copiedPolicy, setCopiedPolicy] = useState(null);
  const [expandedFeatures, setExpandedFeatures] = useState({});
  const [expandedRoles, setExpandedRoles] = useState({});
  const [expandedIdentity, setExpandedIdentity] = useState(false);

  const copyToClipboard = async (text, policyId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedPolicy(policyId);
      setTimeout(() => setCopiedPolicy(null), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedPolicy(policyId);
      setTimeout(() => setCopiedPolicy(null), 2000);
    }
  };

  const toggleFeature = (featureKey) => {
    setExpandedFeatures(prev => ({
      ...prev,
      [featureKey]: !prev[featureKey]
    }));
  };

  const toggleRole = (roleIndex) => {
    setExpandedRoles(prev => ({
      ...prev,
      [roleIndex]: !prev[roleIndex]
    }));
  };

  // Normalize feature name to lowercase with hyphens
  // e.g., "AB Experiment" -> "ab-experiment", "Admin - Queries" -> "admin-queries"
  const normalizeFeatureName = (featureName) => {
    if (!featureName) return '';
    return featureName
      .toLowerCase()
      .replace(/\s*-\s*/g, '-') // Replace " - " or " -" or "- " with "-"
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/[^a-z0-9-]/g, '') // Remove any non-alphanumeric characters except hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
      .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
  };

  // Get features to display based on current feature name
  const getFeaturesToDisplay = () => {
    if (!mappingData || !features) return [];

    const currentRoute = location.pathname;

    // On home page, show all features
    if (currentRoute === '/') {
      return Object.keys(mappingData).map(key => ({
        key,
        feature: mappingData[key]
      }));
    }

    // Find the current feature from features context by matching route
    const currentFeature = features.find(
      (feature) => feature?.route === currentRoute ||
        currentRoute.startsWith(feature?.route + '/') ||
        (feature?.route?.split('/')[1] && currentRoute.split('/')[1] === feature?.route?.split('/')[1])
    );

    if (!currentFeature?.feature_name) {
      return [];
    }

    // Normalize the feature name before matching
    const normalizedFeatureName = normalizeFeatureName(currentFeature.feature_name);

    // Match by normalized feature_name instead of route
    const matchingFeatures = [];

    Object.keys(mappingData).forEach(key => {
      const mappingFeature = mappingData[key];
      const normalizedKey = normalizeFeatureName(key);
      const normalizedMappingFeatureName = normalizeFeatureName(mappingFeature.feature_name);
      
      // Check if normalized feature_name matches (either key or feature_name property)
      if (normalizedKey === normalizedFeatureName || 
          normalizedMappingFeatureName === normalizedFeatureName) {
        matchingFeatures.push({
          key,
          feature: mappingFeature
        });
      }
    });

    return matchingFeatures;
  };

  if (!isOpen) return null;

  return (
    <div className='permissions-modal-overlay' onClick={onClose}>
      <div
        className='permissions-modal-content'
        onClick={(e) => e.stopPropagation()}
      >
        <div className='permissions-modal-header'>
          <h4 className='permissions-modal-title'>
            <i className='fa fa-shield' style={{ marginRight: '8px', color: '#ec4899' }} />
            Used Policy Information
          </h4>
          <button
            className='permissions-modal-close'
            onClick={onClose}
            aria-label='Close'
          >
            <i className='fa fa-times' />
          </button>
        </div>
        <div className='permissions-modal-body'>
          <div className='permissions-policy-section'>
            {/* Identity and Principal Accordion */}
            <div className='permissions-policy-item'>
              <div className={`permissions-identity-accordion-item ${expandedIdentity ? 'expanded' : ''}`}>
                <div
                  className='permissions-identity-accordion-header'
                  onClick={() => setExpandedIdentity(!expandedIdentity)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setExpandedIdentity(!expandedIdentity);
                    }
                  }}
                  aria-expanded={expandedIdentity}
                >
                  <div className='permissions-identity-accordion-header-content'>
                    <i className={`fa fa-chevron-${expandedIdentity ? 'down' : 'right'}`} style={{ marginRight: '12px', fontSize: '12px', transition: 'transform 0.2s ease' }} />
                    <label className='permissions-policy-label'>Identity and Principal</label>
                  </div>
                </div>
                {expandedIdentity && (
                  <div className='permissions-identity-accordion-content'>
                    {user?.email && (
                      <div className='permissions-identity-item'>
                        <label className='permissions-identity-label'>Identity:</label>
                        <div className='permissions-policy-value'>
                          {user.email}
                        </div>
                      </div>
                    )}
                    {user?.roles && user.roles.length > 0 && (
                      <div className='permissions-identity-item'>
                        <label className='permissions-identity-label'>Assumed Roles:</label>
                        <div className='permissions-roles-accordion'>
                          {user.roles.map((role, index) => {
                            const isExpanded = expandedRoles[index];
                            return (
                              <div key={index} className={`permissions-role-accordion-item ${isExpanded ? 'expanded' : ''}`}>
                                <div
                                  className='permissions-role-accordion-header'
                                  onClick={() => toggleRole(index)}
                                  role="button"
                                  tabIndex={0}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      toggleRole(index);
                                    }
                                  }}
                                  aria-expanded={isExpanded}
                                >
                                  <div className='permissions-role-accordion-header-content'>
                                    <i className={`fa fa-chevron-${isExpanded ? 'down' : 'right'}`} style={{ marginRight: '12px', fontSize: '12px', transition: 'transform 0.2s ease' }} />
                                    <span className='permissions-role-name'>{role.role}</span>
                                  </div>
                                </div>
                                {isExpanded && role.mrn && (
                                  <div className='permissions-role-accordion-content'>
                                    <div className='permissions-role-mrn-container'>
                                      <label className='permissions-role-mrn-label'>MRN:</label>
                                      <div className='permissions-role-mrn-value'>{role.mrn}</div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Features Accordion */}
            {(() => {
              const featuresToDisplay = getFeaturesToDisplay();
              if (featuresToDisplay.length === 0) return null;

              return (
                <div className='permissions-policy-item'>
                  <label className='permissions-policy-label'>
                    {location.pathname === '/' ? 'All Features & Policies:' : 'Matching Features & Policies:'}
                  </label>
                  <div className='features-accordion-modal'>
                    {featuresToDisplay.map(({ key: featureKey, feature }) => {
                      const isExpanded = expandedFeatures[featureKey];

                      return (
                        <div key={featureKey} className={`feature-accordion-item ${isExpanded ? 'expanded' : ''}`}>
                          <div
                            id={`feature-header-${featureKey}`}
                            className="feature-accordion-header"
                            onClick={() => toggleFeature(featureKey)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                toggleFeature(featureKey);
                              }
                            }}
                            aria-expanded={isExpanded}
                            aria-controls={`feature-content-${featureKey}`}
                          >
                            <div className="feature-accordion-header-content">
                              <i className={`fa fa-chevron-${isExpanded ? 'down' : 'right'}`} style={{ marginRight: '12px', fontSize: '12px', transition: 'transform 0.2s ease' }} />
                              <span className="feature-accordion-feature-name">{feature.feature_name}</span>
                            </div>
                          </div>
                          {isExpanded && (
                            <div
                              className="feature-accordion-content"
                              id={`feature-content-${featureKey}`}
                              role="region"
                              aria-labelledby={`feature-header-${featureKey}`}
                            >
                              {Object.keys(feature.mapping).map((actionKey) => (
                                <div key={actionKey} className="feature-action-group">
                                  <div className="feature-action-title">
                                    <i className="fa fa-key" style={{ marginRight: '8px', color: '#6c757d' }} />
                                    <strong>{actionKey.charAt(0).toUpperCase() + actionKey.slice(1)}:</strong>
                                  </div>
                                  <div className="feature-policies-list">
                                    {feature.mapping[actionKey].map((policy, index) => {
                                      const policyId = `${featureKey}-${actionKey}-${index}`;
                                      const isCopied = copiedPolicy === policyId;
                                      return (
                                        <div key={index} className="feature-policy-item">
                                          <div className="feature-policy-name-wrapper">
                                            <div className="feature-policy-name">{policy.policy}</div>
                                            <div className="feature-policy-actions">
                                              {policy.severity && (
                                                <span className={`feature-policy-severity severity-${policy.severity}`}>
                                                  {policy.severity}
                                                </span>
                                              )}
                                              <button
                                                className="feature-copy-button"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  copyToClipboard(policy.policy, policyId);
                                                }}
                                                title="Copy to clipboard"
                                                aria-label="Copy policy to clipboard"
                                              >
                                                <i className={`fa ${isCopied ? 'fa-check' : 'fa-copy'}`} />
                                              </button>
                                            </div>
                                          </div>
                                          <div className="feature-policy-description">{policy.description}</div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PermissionsModal;

